import { useState, useRef, useEffect } from 'react';

export type Track = {
    id: string;
    title: string;
    duration: number;
    previewURL: string;
    artist: string;
    artistImagePath: string;
    artworkPath: string;
};

export function useAudioPlayer(tracks: Track[], autoAdvance: boolean = true, autoPlay: boolean = true) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(new Audio());

    const currentTrack = tracks[currentIndex];

    useEffect(() => {
        const audio = audioRef.current;
        const track = tracks[currentIndex];
        if (!track?.previewURL) return;
        audio.src = track?.previewURL ?? '';
        audio.load();
        setProgress(0);

        if (autoPlay) {
            // auto play when a new track is loaded
            audio.play().then(() => setIsPlaying(true)).catch((error: any) => { console.log(error) })
        } else {
            setIsPlaying(false);
            audio.pause();
        }
    }, [currentIndex, tracks, autoPlay]);

    useEffect(() => {
        const audio = audioRef.current;

        const onTimeUpdate = () => {
            setProgress((audio.currentTime / audio.duration) * 100);
        };

        const onEnded = () => {
            setIsPlaying(false);
            setProgress(100);
            if (autoAdvance && currentIndex < tracks.length - 1) {
                setCurrentIndex(i => i + 1);
            }
        };

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
        };
    }, [currentIndex, tracks.length, autoAdvance]);

    useEffect(() => {
        const audio = audioRef.current;

        return () => {
            audio.pause();
            audio.src = '';
        };
    }, []);

    const play = async () => {
        await audioRef.current.play();
        setIsPlaying(true);
    };

    const pause = () => {
        audioRef.current.pause();
        setIsPlaying(false);
    };

    const toggle = () => isPlaying ? pause() : play();

    const goTo = (index: number) => setCurrentIndex(index);
    // const next = () => { if (currentIndex < tracks.length - 1) setCurrentIndex(i => i + 1); };
    const prev = () => { if (currentIndex > 0) setCurrentIndex(i => i - 1); };

    return {
        currentTrack,
        currentIndex,
        isPlaying,
        progress,
        toggle,
        prev,
        goTo,
        setCurrentIndex,
    };
}